import * as React from 'react';
import { getFileExtension } from '@deltasierra/shared';
import { LightboxMediaType } from '../lightbox-context';
import { Image } from '../../Image';
import { LightboxDocument } from './LightboxDocument';
import { LightboxDocumentPdf } from './LightboxDocumentPdf';
import { LightboxVideo } from './LightboxVideo';
import { LightboxUnsupportedType } from './LightboxUnsupportedType';
import { LightboxDangerWarning } from './LightboxDangerWarning';

export interface LightboxMediaProps {
    alt: string;
    elementRef?: React.ForwardedRef<HTMLDivElement | null>;
    src?: string;
    type?: LightboxMediaType;
    onLoadFail?: React.ReactEventHandler<HTMLImageElement>;
}

export function LightboxMedia({ alt, elementRef, onLoadFail, src = '', type }: LightboxMediaProps): JSX.Element {
    const fileExtension = getFileExtension(src);

    const isFileDangerous = (extension: string) => {
        const dangerousExtensions = ['exe', 'bat', 'cmd', 'msi', 'vbs'];
        return dangerousExtensions.includes(extension.toLowerCase());
    };

    return (
        <div
            data-testid="lightbox-media-container"
            ref={elementRef}
            style={{ alignItems: 'center', display: 'flex', justifyContent: 'center' }}
        >
            {isFileDangerous(fileExtension) ? (
                <LightboxDangerWarning />
            ) : (
                <>
                    {type === LightboxMediaType.Image && (
                        <Image
                            alt={alt}
                            shouldUseLoadingFallback
                            src={src}
                            style={{ maxHeight: '80vh' }}
                            title={alt}
                            onError={onLoadFail}
                        />
                    )}
                    {type === LightboxMediaType.Video && <LightboxVideo controls src={src} />}
                    {type === LightboxMediaType.Document && fileExtension === 'pdf' && (
                        <LightboxDocumentPdf data={`${src}?#view=fit`} />
                    )}
                    {type === LightboxMediaType.Document && fileExtension !== 'pdf' && (
                        <LightboxDocument src={src} />
                    )}
                    {type === LightboxMediaType.Unsupported && (
                        <LightboxUnsupportedType fileType={fileExtension} src={src} />
                    )}
                </>
            )}
        </div>
    );
}
LightboxMedia.displayName = 'LightboxMedia';
LightboxMedia.MediaType = LightboxMediaType;
